import { useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";

/** "Connect wallet" modale: in realtà signup/login via email per MVP — i 5
 *  provider crypto (Phantom/MetaMask/etc) restano placeholder finché non
 *  cabliamo wagmi nel round successivo. */
export function WalletModal() {
  const open = useStore((s) => s.walletOpen);
  const setOpen = useStore((s) => s.setWalletOpen);
  const setUser = useStore((s) => s.setUser);
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("IT");
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    setErr(null);
    setSubmitting(true);
    try {
      if (tab === "signup") {
        const { data, error } = await api.POST("/v1/auth/signup", {
          body: { email, password, country },
        });
        if (error) {
          setErr(extractMsg(error) ?? "Signup failed");
          return;
        }
        if (data) {
          setUser(data.id, data.email);
          setOpen(false);
        }
      } else {
        const { data, error } = await api.POST("/v1/auth/login", {
          body: { email, password },
        });
        if (error) {
          setErr(extractMsg(error) ?? "Invalid credentials");
          return;
        }
        if (data) {
          setUser(data.user_id, email);
          setOpen(false);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-[440px] max-w-[92vw] rounded-modal border border-line bg-bg-1 p-6 shadow-[var(--shadow-card)]">
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-semibold">Connect</h2>
          <button
            className="btn-ghost h-9 w-9 rounded-full p-0"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <Icon.X size={16} />
          </button>
        </div>

        <div className="mt-3 mb-4 inline-flex w-full">
          <button
            className={`flex-1 rounded-l-[10px] border border-line py-2 text-[13px] ${
              tab === "signin" ? "bg-bg-3 text-text" : "text-muted"
            }`}
            onClick={() => setTab("signin")}
          >
            Sign in
          </button>
          <button
            className={`flex-1 rounded-r-[10px] border border-l-0 border-line py-2 text-[13px] ${
              tab === "signup" ? "bg-bg-3 text-text" : "text-muted"
            }`}
            onClick={() => setTab("signup")}
          >
            Create account
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              Email
            </span>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
              Password
            </span>
            <input
              type="password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete={tab === "signin" ? "current-password" : "new-password"}
            />
          </label>
          {tab === "signup" && (
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted">
                Country
              </span>
              <input
                type="text"
                className="input font-mono uppercase"
                value={country}
                onChange={(e) =>
                  setCountry(e.target.value.toUpperCase().slice(0, 2))
                }
                placeholder="IT"
                maxLength={2}
              />
            </label>
          )}
          {err && (
            <div className="rounded-[10px] border border-no/40 bg-no/10 px-3 py-2 text-[12.5px] text-no">
              {err}
            </div>
          )}
          <button
            className="btn btn-primary w-full !py-3"
            disabled={submitting || !email || !password}
            onClick={submit}
          >
            {submitting
              ? "…"
              : tab === "signin"
                ? "Sign in"
                : "Create account"}
          </button>
        </div>

        <p className="mt-4 text-[11px] text-muted">
          By continuing you agree to the Terms of Service & acknowledge trading risks.
        </p>
      </div>
    </div>
  );
}

function extractMsg(err: unknown): string | null {
  if (typeof err === "object" && err && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return null;
}
